import { useState } from 'react';
import {
  HelpCircle, ChevronDown, ChevronRight, BookOpen, Zap, Trophy, Users,
  BarChart3, Settings, Star, MessageCircle, Swords, ArrowRight, Target,
  UserPlus, Shield, Link2, Mail
} from 'lucide-react';

const SECTIONS = [
  { id: 'getting-started', label: 'Getting Started', icon: BookOpen },
  { id: 'game-formats', label: 'Game Formats', icon: Swords },
  { id: 'scoring', label: 'Scoring', icon: Target },
  { id: 'ratings', label: 'Ratings Explained', icon: BarChart3 },
  { id: 'tournaments', label: 'Tournament Setup', icon: Trophy },
  { id: 'sessions-playoffs', label: 'Sessions & Playoffs', icon: Zap },
  { id: 'players', label: 'Players & Nicknames', icon: Users },
  { id: 'discovery', label: 'Discovery & Invites', icon: UserPlus },
  { id: 'roles', label: 'Roles & Permissions', icon: Shield },
  { id: 'clubs', label: 'Clubs & Invites', icon: Link2 },
  { id: 'notifications', label: 'Notifications', icon: Star },
  { id: 'admin-settings', label: 'Admin Settings', icon: Settings },
  { id: 'faq', label: 'FAQ', icon: MessageCircle },
];

function Accordion({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`accordion-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-white hover:bg-slate-50 transition-colors text-left"
      >
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>
      {open && <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">{children}</div>}
    </div>
  );
}

function SectionCard({ id, title, icon: Icon, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`help-section-${id}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
          <Icon size={18} className="text-green-600" />
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h2>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function P({ children }) {
  return <p className="text-sm text-slate-600 leading-relaxed">{children}</p>;
}

function Tip({ children }) {
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 flex gap-2">
      <Zap size={14} className="text-green-600 flex-shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}

export default function HelpPage() {
  const [activeSection, setActiveSection] = useState('getting-started');

  const scrollTo = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="help-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Help & Documentation
        </h1>
        <p className="text-slate-500 mt-1 text-sm">Everything you need to know about Jump Smash Game Manager</p>
      </div>

      <div className="flex gap-8">
        {/* Sidebar Navigation */}
        <nav className="hidden lg:block w-56 flex-shrink-0 sticky top-24 self-start" data-testid="help-sidebar-nav">
          <div className="space-y-0.5">
            {SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-all ${
                  activeSection === s.id
                    ? 'bg-green-50 text-green-700 font-semibold'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <s.icon size={14} />
                {s.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-10">
          {/* ── GETTING STARTED ── */}
          <SectionCard id="getting-started" title="Getting Started" icon={BookOpen}>
            <P>Jump Smash is a multi-tenant badminton tournament manager. Here's how to get going in 3 steps:</P>
            <div className="grid sm:grid-cols-3 gap-3 mt-2">
              {[
                { step: '1', title: 'Sign Up & Assess', desc: 'Register with your email, location (country/state/city), and nickname. Complete the 10-question skill assessment to get your initial rating.' },
                { step: '2', title: 'Join or Create a Club', desc: 'Discover nearby clubs and request to join, accept an admin invite, or create your own club with a location.' },
                { step: '3', title: 'Play & Track', desc: 'Join tournaments, play games across sessions, track your rating progress, and compete in playoffs!' },
              ].map(s => (
                <div key={s.step} className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <div className="w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold mb-2">{s.step}</div>
                  <p className="text-sm font-semibold text-slate-800 mb-1">{s.title}</p>
                  <p className="text-xs text-slate-500">{s.desc}</p>
                </div>
              ))}
            </div>
            <Accordion title="How do I navigate the app?" defaultOpen>
              <p>After logging in and selecting a club, you'll see the <strong>sidebar</strong> with these sections:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Dashboard</strong> — Overview of your club: player count, tournaments, recent activity.</li>
                <li><strong>Players</strong> — View and manage all players. Add/remove with +/− buttons. (Admin/Contributor only)</li>
                <li><strong>Tournaments</strong> — Create, manage, and view tournaments with sessions and playoffs.</li>
                <li><strong>Standings</strong> — Club-wide player rankings with podium. Ratings shown in brackets.</li>
                <li><strong>Invites</strong> — View incoming club invitations and outgoing join requests.</li>
                <li><strong>Settings</strong> — Update nickname, enable push notifications, change password.</li>
                <li><strong>Admin</strong> — Manage users, find & invite players, approve join requests, send announcements, configure app settings. (Admin only)</li>
                <li><strong>Help</strong> — You're here!</li>
              </ul>
            </Accordion>
            <Accordion title="How do I switch between clubs?">
              <p>Click the <strong>club name</strong> at the top of the sidebar, then click the switch icon. This takes you back to the club selection page where you can pick a different club or create a new one.</p>
            </Accordion>
          </SectionCard>

          {/* ── GAME FORMATS ── */}
          <SectionCard id="game-formats" title="Game Formats" icon={Swords}>
            <P>When creating a tournament, you choose one of four game formats. Each format determines how teams are formed for every game.</P>
            <div className="space-y-3 mt-2">
              {[
                {
                  title: 'Singles (1v1)',
                  when: 'When you want individual matchups',
                  desc: 'One player vs one player. Ratings are tracked using singles-specific ratings. Best for competitive individual play.',
                  color: 'bg-blue-50 border-blue-200 text-blue-800',
                },
                {
                  title: 'Doubles — Rotating Partners',
                  when: 'Most popular for casual/social play',
                  desc: 'Partners change every game. The system tries to pair players who haven\'t played together recently and balances teams by rating. Great for social sessions where everyone gets to play with everyone.',
                  color: 'bg-purple-50 border-purple-200 text-purple-800',
                },
                {
                  title: 'Doubles — Fixed Partners (Assigned)',
                  when: 'When you want to manually control pairings',
                  desc: 'The tournament admin manually assigns who partners with whom. Once set, these pairs play together for the entire tournament. Ideal for competitive team events.',
                  color: 'bg-amber-50 border-amber-200 text-amber-800',
                },
                {
                  title: 'Doubles — Fixed Partners (Random)',
                  when: 'Fun, luck-of-the-draw pairing',
                  desc: 'The system randomly assigns partners that stay together for the whole tournament. A fun way to mix things up for social events.',
                  color: 'bg-teal-50 border-teal-200 text-teal-800',
                },
              ].map(f => (
                <div key={f.title} className={`rounded-lg border p-4 ${f.color}`}>
                  <p className="font-semibold text-sm">{f.title}</p>
                  <p className="text-xs mt-0.5 opacity-80">Best for: {f.when}</p>
                  <p className="text-xs mt-2 opacity-90">{f.desc}</p>
                </div>
              ))}
            </div>
            <Tip>Not sure which to pick? <strong>Doubles — Rotating Partners</strong> is the most popular choice for regular club sessions.</Tip>
          </SectionCard>

          {/* ── SCORING ── */}
          <SectionCard id="scoring" title="Scoring" icon={Target}>
            <Accordion title="How do I enter scores?" defaultOpen>
              <ol className="list-decimal list-inside space-y-1.5">
                <li>Go to your tournament and open the <strong>Games</strong> tab.</li>
                <li>Find the game under "Open Games" — it shows both teams with a green <strong>"Tap to Score"</strong> badge.</li>
                <li>Click the game card to open the <strong>full-screen score entry</strong> modal.</li>
                <li>Enter each team's score in the large number inputs.</li>
                <li>Click <strong>"Save Score"</strong>. The game is marked as completed and ratings are updated immediately.</li>
              </ol>
            </Accordion>
            <Accordion title="How does winning work?">
              <p>Jump Smash uses a <strong>higher score wins</strong> system. There is no fixed point limit — just enter whatever the final scores are. The team with the higher score wins. Scores cannot be equal (no ties).</p>
              <p className="mt-2">For example: 21-18, 15-12, or even 30-28 are all valid. You decide when a game ends.</p>
            </Accordion>
            <Accordion title="Can I edit a score after saving?">
              <p>Yes. Admins and contributors can tap a completed game card to re-open the score entry. The score will be corrected, but <strong>ELO ratings are NOT recalculated</strong> on edits — only the displayed score changes. This preserves rating integrity. All edits are logged in the <strong>Score Audit Trail</strong>.</p>
            </Accordion>
            <Accordion title="What is the Score Audit Trail?">
              <p>Every score entry and correction is logged with who made the change, the old score, the new score, and when it happened. Admins can view this trail for full transparency.</p>
            </Accordion>
          </SectionCard>

          {/* ── RATINGS ── */}
          <SectionCard id="ratings" title="Ratings Explained" icon={BarChart3}>
            <P>Jump Smash uses the <strong>ELO rating system</strong> — the same system used in chess — adapted for badminton. Your rating reflects your skill level relative to other players.</P>

            <Accordion title="The three rating tiers" defaultOpen>
              <div className="space-y-3">
                <div className="flex gap-3 items-start">
                  <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">Tournament Rating</p>
                    <p className="text-xs text-slate-500">Tracks your performance within a single tournament. Resets at the start of each tournament based on the rating type you choose.</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">Club Rating</p>
                    <p className="text-xs text-slate-500">Your cumulative rating within a specific club. Updated after every non-fun game across all tournaments in that club. Fun tournament games do not affect your Club Rating.</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="w-2 h-2 rounded-full bg-purple-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800">Lifetime Rating</p>
                    <p className="text-xs text-slate-500">Your global rating across ALL clubs and all non-fun tournaments. Whenever you play a rated game in any club, your Lifetime Rating updates and automatically syncs across all your other club memberships. Fun tournament games never count toward your Lifetime Rating.</p>
                  </div>
                </div>
              </div>
            </Accordion>

            <Accordion title="How is ELO calculated?">
              <p>After each game, ratings change based on:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Result</strong> — Did you win or lose?</li>
                <li><strong>Expected outcome</strong> — Were you expected to win based on ratings?</li>
                <li><strong>Rating gap</strong> — How big is the difference between teams?</li>
              </ul>
              <div className="bg-slate-50 rounded-lg p-3 mt-3 text-xs font-mono">
                <p className="text-slate-500 mb-1">// Simplified formula</p>
                <p>New Rating = Old Rating + K × (Actual - Expected)</p>
                <p className="mt-1 text-slate-400">K = 32 (standard), Actual = 1 (win) or 0 (loss)</p>
                <p className="text-slate-400">Expected = 1 / (1 + 10^((OpponentRating - YourRating) / 400))</p>
              </div>
              <p className="mt-3">In plain English:</p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li><strong>Beat a stronger opponent</strong> → You gain MORE points</li>
                <li><strong>Beat a weaker opponent</strong> → You gain fewer points</li>
                <li><strong>Lose to a stronger opponent</strong> → You lose fewer points</li>
                <li><strong>Lose to a weaker opponent</strong> → You lose MORE points</li>
              </ul>
            </Accordion>

            <Accordion title="What does my rating number mean?">
              <div className="space-y-1.5 mt-1">
                {[
                  { range: 'Below 1400', level: 'Beginner', color: 'text-slate-500' },
                  { range: '1400–1500', level: 'Intermediate', color: 'text-blue-600' },
                  { range: '1500–1600', level: 'Upper Intermediate', color: 'text-green-600' },
                  { range: '1600–1700', level: 'Advanced', color: 'text-amber-600' },
                  { range: '1700+', level: 'Expert', color: 'text-red-600' },
                ].map(r => (
                  <div key={r.range} className="flex items-center justify-between text-xs">
                    <span className="font-mono text-slate-700">{r.range}</span>
                    <span className={`font-semibold ${r.color}`}>{r.level}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">Your initial rating is set by the Skill Assessment questionnaire you complete after signing up.</p>
            </Accordion>

            <Accordion title="What is 'Provisional' vs 'Verified' rating?">
              <p>When you first get your rating (from the questionnaire), it's marked as <strong>Provisional</strong>. This means:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Your rating adjusts <strong>faster</strong> (K=48 instead of K=32) to quickly correct any over/under-estimation.</li>
                <li>After playing <strong>25 rated games</strong> against <strong>15 unique opponents</strong>, your rating becomes <strong>Verified</strong>.</li>
                <li>Verified ratings move more slowly and are considered stable and accurate.</li>
              </ul>
              <p className="mt-2">This system ensures that even if you rate yourself too high or too low in the questionnaire, your rating corrects itself within about 10-15 games.</p>
            </Accordion>

            <Accordion title="How do I see my rating changes?">
              <p>After a game is completed, click <strong>"View Rating Changes"</strong> below the game card. This opens a panel showing each player's rating change for Tournament, Club, and Lifetime ratings — with before/after values and the +/- change.</p>
            </Accordion>

            <Tip>Ratings are separate for singles and doubles. Playing doubles won't affect your singles rating, and vice versa.</Tip>
          </SectionCard>

          {/* ── TOURNAMENTS ── */}
          <SectionCard id="tournaments" title="Tournament Setup" icon={Trophy}>
            <Accordion title="How do I create a tournament?" defaultOpen>
              <ol className="list-decimal list-inside space-y-1.5">
                <li>Go to <strong>Tournaments</strong> in the sidebar.</li>
                <li>Click <strong>"+ Create Tournament"</strong>.</li>
                <li>Fill in: Name, Start Date, Number of Courts, Game Type, and Rating Type.</li>
                <li>Click <strong>Create</strong>.</li>
                <li>Go to the <strong>Players</strong> tab to add players.</li>
                <li>Switch to <strong>Games</strong> tab and click <strong>"Generate Games"</strong>.</li>
              </ol>
            </Accordion>

            <Accordion title="What is 'Rating Type for Pairing'?">
              <p>When creating a tournament, you choose which rating to use for generating balanced matchups:</p>
              <ul className="list-disc list-inside mt-2 space-y-1.5">
                <li><strong>Lifetime Ratings</strong> — Uses each player's global lifetime rating to seed the tournament. Best for competitive events.</li>
                <li><strong>Club Ratings</strong> — Uses the player's rating within the current club. Good for club-internal competitions.</li>
                <li><strong>Tournament Only</strong> — Everyone starts at 1500. Ratings are isolated to this tournament. Good for fresh-start or one-off events.</li>
              </ul>
            </Accordion>

            <Accordion title="How does game generation work?">
              <p>When you click "Generate Games", the system:</p>
              <ol className="list-decimal list-inside mt-2 space-y-1">
                <li>Takes all <strong>available</strong> players (toggle availability on the Players tab).</li>
                <li>For <strong>Doubles Changing</strong>: groups players by rating into (courts−1) groups, then mixes partners within each group for skill-balanced matchups.</li>
                <li>Avoids repeating recent pairings where possible.</li>
                <li><strong>Smart court assignment</strong>: free courts are filled first, then busy courts get new games.</li>
              </ol>
              <p className="mt-2">The number of games <strong>defaults to the number of courts</strong> — so 2 courts generates 2 games. You can adjust this.</p>
              <p className="mt-1 text-xs text-slate-500">Player names display with their club rating in brackets (e.g., "Sateesh (1520)") throughout games and standings.</p>
            </Accordion>

            <Accordion title="What is the Bench?">
              <p>When games are generated, not all players may get assigned to a game (especially if there are more players than courts × 4). The <strong>"On Bench"</strong> section shows which available players are currently waiting. This helps you plan the next round — generate another batch when current games finish.</p>
            </Accordion>

            <Accordion title="How do I manage partner pairs?">
              <p>For <strong>Fixed Partners</strong> tournaments, go to the <strong>Players</strong> tab and click <strong>"Manage Partners"</strong>. You can:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><strong>Auto-suggest</strong> — System pairs strongest with weakest for balance.</li>
                <li><strong>Random</strong> — System randomly assigns partners.</li>
                <li><strong>Manual</strong> — Select two players and pair them yourself.</li>
              </ul>
            </Accordion>
          </SectionCard>

          {/* ── SESSIONS & PLAYOFFS ── */}
          <SectionCard id="sessions-playoffs" title="Sessions & Playoffs" icon={Zap}>
            <Accordion title="What are Sessions?" defaultOpen>
              <p>A <strong>Session</strong> groups all games played in one continuous stretch of play — like a 2-hour club night. A single tournament can span days, weeks, or months with many sessions, each one capturing a distinct playing date.</p>
              <ol className="list-decimal list-inside mt-2 space-y-1">
                <li>In the <strong>Games</strong> tab, click <strong>"Start Session"</strong> (admin/contributor only) at the beginning of each playing session.</li>
                <li>Sessions are auto-named: Session 1, Session 2, etc. The start date and time are recorded automatically.</li>
                <li>Generate games as usual — all games created while a session is active are tagged to that session.</li>
                <li>Enter scores for all games played in that sitting.</li>
                <li>Click <strong>"End Session"</strong> when play is done for the day. The session chip shows how many games were played and the session is sealed with an end timestamp.</li>
              </ol>
              <p className="mt-2">Each session is a permanent record: standings, rating changes, and game history are all tied to the session date, giving you a full audit trail of every club night across the tournament's lifetime.</p>
            </Accordion>

            <Accordion title="What are Playoffs?">
              <p>At any point during a tournament, admins can create a <strong>Playoff</strong> — a final competition using current standings.</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Go to the <strong>Playoffs</strong> tab and click <strong>"Create Playoff"</strong>.</li>
                <li>Players are divided into groups using a <strong>snake draft</strong> based on current tournament ratings.</li>
                <li>Each group must have <strong>minimum 4 players</strong>. If a group would have fewer, players are redistributed to other groups.</li>
                <li>Courts are <strong>equally distributed</strong> across groups (e.g., 2 groups, 5 courts = 3+2 courts per group).</li>
                <li>Click <strong>"Generate Playoff Games"</strong> to create matchups within each group.</li>
              </ul>
            </Accordion>

            <Accordion title="Do playoff games affect ratings?">
              <p>Yes! Playoff games affect <strong>all three rating tiers</strong> (Tournament, Club, Lifetime) just like regular tournament games. The only exception is <strong>Fun Tournaments</strong>, where Club and Lifetime ratings are unaffected (tournament-internal standings still update).</p>
            </Accordion>

            <Accordion title="What is a Fun Tournament?">
              <p>When creating a tournament, check <strong>"Fun Tournament (no rating impact)"</strong>. Fun tournaments work like this:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Scores are tracked</strong> normally and appear in the Games tab.</li>
                <li><strong>Tournament standings update</strong> — internal tournament ratings change so players can see how they rank within the event and games remain skill-balanced.</li>
                <li><strong>Club Ratings are NOT affected</strong> — no permanent club-level rating change.</li>
                <li><strong>Lifetime Ratings are NOT affected</strong> — playing a fun tournament has zero impact on your global rating.</li>
              </ul>
              <p className="mt-2">Great for casual/social events, tryouts, or practice sessions where you want to track performance without affecting anyone's competitive rating.</p>
            </Accordion>
          </SectionCard>

          {/* ── PLAYERS & NICKNAMES ── */}
          <SectionCard id="players" title="Players & Nicknames" icon={Users}>
            <Accordion title="What is a nickname?" defaultOpen>
              <p>Every player has three identifiers:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Full Name</strong> — Your real name (e.g., "Sateesh Ganesh"). Used in emails and profiles.</li>
                <li><strong>Username</strong> — Used for login (e.g., "sateesh42").</li>
                <li><strong>Nickname</strong> — A short display name (e.g., "Sateesh") used in game cards, score entry, brackets, and the bench display.</li>
              </ul>
              <p className="mt-2">Nicknames must be <strong>unique</strong> across the entire system — no two players can share the same nickname.</p>
            </Accordion>

            <Accordion title="How do I view singles vs doubles ratings on the Players page?">
              <p>The club <strong>Players</strong> tab has a <strong>Doubles / Singles</strong> dropdown next to the search bar (defaults to Doubles). Selecting a type switches all table columns to that game type:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Club Rating</strong> — Your rating within this club for the selected game type.</li>
                <li><strong>Lifetime</strong> — Your global rating for that game type across all clubs.</li>
                <li><strong>Games</strong> — Total games of that type played in this club.</li>
                <li><strong>W/L</strong> — Wins and losses for that game type in this club.</li>
              </ul>
            </Accordion>

            <Accordion title="What are Guest players?">
              <p>Guest players are for people who don't have an email or phone — like <strong>kids or casual visitors</strong>. When adding a new player from the club's Players tab:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Click <strong>"Manage Players"</strong>, then <strong>"Create New Player"</strong>.</li>
                <li>Check the <strong>"Guest (no email/phone)"</strong> checkbox.</li>
                <li>You only need to provide: Full Name, Nickname, Username, and Password.</li>
                <li>Guest players can play in games and have ratings just like regular players.</li>
              </ul>
            </Accordion>

            <Accordion title="How do I add players to a tournament?">
              <p>In the tournament's <strong>Players</strong> tab, click <strong>"Manage Players"</strong>. This shows all players already in the club — search and click <strong>"+"</strong> to add them to the tournament.</p>
              <p className="mt-2"><strong>To create a brand-new player</strong>, use the <strong>"Manage Players"</strong> button on the club's <strong>Players</strong> tab first. Once created there, they'll appear in the tournament player list.</p>
            </Accordion>

            <Accordion title="What about unique email and phone?">
              <p>Each email and phone number can only belong to <strong>one user</strong> in the system. This prevents duplicate accounts. If you see "Email already registered" or "Phone number already in use", it means another account already uses that contact info.</p>
            </Accordion>
          </SectionCard>

          {/* ── ROLES & PERMISSIONS ── */}
          <SectionCard id="roles" title="Roles & Permissions" icon={Shield}>
            <P>Each club member has one of three roles that determine what they can do:</P>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-3 py-2 font-semibold text-slate-600 border-b border-slate-200">Action</th>
                    <th className="text-center px-3 py-2 font-semibold text-green-700 border-b border-slate-200">Admin</th>
                    <th className="text-center px-3 py-2 font-semibold text-blue-700 border-b border-slate-200">Contributor</th>
                    <th className="text-center px-3 py-2 font-semibold text-slate-600 border-b border-slate-200">Player</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['View tournaments, standings, games', true, true, true],
                    ['Join tournaments', true, true, true],
                    ['Create & manage tournaments', true, true, false],
                    ['Add/remove players (non-admin)', true, true, false],
                    ['Enter & edit scores', true, true, false],
                    ['Generate games', true, true, false],
                    ['Start/end sessions', true, true, false],
                    ['Create invite links', true, false, false],
                    ['Send invite emails', true, false, false],
                    ['Manage club settings', true, false, false],
                    ['Wipe database (danger)', true, false, false],
                  ].map(([action, admin, contrib, player], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="px-3 py-2 text-slate-700 border-b border-slate-100">{action}</td>
                      <td className="text-center px-3 py-2 border-b border-slate-100">{admin ? <span className="text-green-600 font-bold">Yes</span> : <span className="text-slate-300">—</span>}</td>
                      <td className="text-center px-3 py-2 border-b border-slate-100">{contrib ? <span className="text-blue-600 font-bold">Yes</span> : <span className="text-slate-300">—</span>}</td>
                      <td className="text-center px-3 py-2 border-b border-slate-100">{player ? <span className="text-slate-600 font-bold">Yes</span> : <span className="text-slate-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>

          {/* ── CLUBS & INVITES ── */}
          <SectionCard id="clubs" title="Clubs & Invites" icon={Link2}>
            <Accordion title="How do I create a club?" defaultOpen>
              <p>On the <strong>My Clubs</strong> page (shown after login), click <strong>"+ Create Club"</strong>. Give it a name, optional description, and its <strong>location</strong> (country / state / city). Location powers club discovery. You automatically become the club's admin.</p>
            </Accordion>
            <Accordion title="How do I invite people?">
              <p>As a club admin, click <strong>"Invite Members"</strong> in the sidebar. Then:</p>
              <ol className="list-decimal list-inside mt-2 space-y-1">
                <li>Choose a role (Player/Contributor/Admin).</li>
                <li>Click <strong>"Create Link"</strong> — a shareable link is created and copied to your clipboard.</li>
                <li>Share the link — anyone with a Jump Smash account can join by opening it.</li>
              </ol>
            </Accordion>
            <Accordion title="Can I send invites by email?">
              <p>Yes! Each invite link has a <strong>mail icon</strong>. Click it, enter the recipient's email, and click <strong>Send</strong>. They'll receive a branded email with a join button. (Requires SMTP to be configured by your admin.)</p>
            </Accordion>
            <Accordion title="How long do invite links last?">
              <p>Invite links expire after <strong>7 days</strong> by default. Admins can revoke any link immediately by clicking the trash icon. Once revoked, the link stops working.</p>
            </Accordion>
            <Accordion title="Where do I see invites sent to me?">
              <p>Pending club invitations appear in two places:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>My Clubs page</strong> — Amber cards appear at the top of the page, above your existing clubs. Click <strong>Join</strong> to accept or <strong>×</strong> to decline. The new club appears immediately after accepting.</li>
                <li><strong>Invites page</strong> (sidebar) — Shows both incoming invites and your outgoing join requests with their current status.</li>
              </ul>
              <Tip>If you're new and haven't created a club yet, check the top of the My Clubs page — your invite will be waiting there.</Tip>
            </Accordion>
          </SectionCard>

          {/* ── DISCOVERY & INVITES ── */}
          <SectionCard id="discovery" title="Discovery & Invites" icon={UserPlus}>
            <P>Discovery connects players with clubs based on <strong>location</strong>. Clubs find local players to invite, and players find clubs near them.</P>

            <Accordion title="How do admins find and invite players?" defaultOpen>
              <ol className="list-decimal list-inside space-y-1.5">
                <li>Open the <strong>Players</strong> tab and click <strong>"Find Players to Invite"</strong>.</li>
                <li>Filter by <strong>name, city, state, country, availability, rating range</strong>.</li>
                <li>Each player card shows their nickname, lifetime rating, city, and availability.</li>
                <li>Click <strong>"Invite"</strong> — the button turns into an amber <strong>"Pending Invite"</strong> badge so you don't accidentally re-invite the same person.</li>
                <li>The player sees the invite in their <strong>Invites</strong> tab and can accept or reject.</li>
              </ol>
              <p className="mt-2 text-xs text-slate-500">Players already in the club are automatically excluded from this list — you'll only see people who aren't members yet.</p>
              <Tip>The Pending Invite badge persists across reloads and sessions — it's backed by the database, not just your browser tab.</Tip>
            </Accordion>

            <Accordion title="How do players discover clubs?">
              <p>On the <strong>My Clubs</strong> page, click <strong>"Discover Other Clubs"</strong>. You'll see clubs filtered to your country/state/city with:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Club name, description, and city</li>
                <li>Member count and rating range (min–max) — player rosters stay private</li>
                <li><strong>"Request to Join"</strong> button — sends a join request to the club's admins</li>
              </ul>
            </Accordion>

            <Accordion title="Where do I manage invites I've received or sent?">
              <p>Click <strong>Invites</strong> in the sidebar. You'll see two sections:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Incoming Invites</strong> — Club invitations from admins. Accept or Reject.</li>
                <li><strong>Outgoing Requests</strong> — Join requests you sent to clubs. Status: pending / approved / rejected.</li>
              </ul>
            </Accordion>

            <Accordion title="How do admins handle join requests?">
              <p>A <strong>"Pending Join Requests"</strong> banner appears on the Admin panel whenever a player requests to join. It shows the player's name and rating. Click <strong>Approve</strong> to add them as a player, or <strong>Reject</strong> to decline.</p>
            </Accordion>
          </SectionCard>

          {/* ── NOTIFICATIONS ── */}
          <SectionCard id="notifications" title="Notifications" icon={Star}>
            <P>Jump Smash delivers both <strong>in-app notifications</strong> (bell icon) and <strong>web push notifications</strong> (desktop/mobile) so you never miss a game, invite, or announcement.</P>

            <Accordion title="How do I enable push notifications?" defaultOpen>
              <ol className="list-decimal list-inside space-y-1.5">
                <li>Open <strong>Settings</strong> in the sidebar.</li>
                <li>Find the <strong>"Push Notifications"</strong> card.</li>
                <li>If the button reads <strong>"Enable Push Notifications"</strong>, click it and allow the browser permission prompt.</li>
                <li>Once enabled, the card shows a green <strong>"Push notifications are enabled"</strong> badge.</li>
              </ol>
              <p className="mt-2 text-xs text-slate-500">Push works on Chrome, Edge, Firefox, and Safari (iOS 16.4+ when app is added to Home Screen).</p>
              <p className="mt-1 text-xs text-slate-400">If the card says <em>"Push notifications are not configured on this server"</em>, the server administrator needs to set up VAPID keys in the backend environment — see the <code>.env.example</code> file for instructions.</p>
            </Accordion>

            <Accordion title="What triggers a push notification?">
              <ul className="list-disc list-inside space-y-1">
                <li>A club admin sends a <strong>Club Announcement</strong> (mass-notify)</li>
                <li>You receive a <strong>club invite</strong></li>
                <li>Your <strong>join request</strong> is approved or rejected</li>
                <li>You're added to a new <strong>game</strong></li>
                <li>A <strong>score</strong> is updated in a game you played</li>
                <li>A nearby player (same city) becomes <strong>available</strong> to play</li>
              </ul>
            </Accordion>

            <Accordion title="How do I see missed notifications?">
              <p>Click the <strong>bell icon</strong> in the sidebar footer — it shows an unread count badge. The dropdown lists your recent notifications. Click one to jump straight to the relevant page (e.g., tournament, invite).</p>
            </Accordion>

            <Accordion title="Can admins broadcast to the whole club?">
              <p>Yes. On the Admin panel click <strong>"Send Club Announcement"</strong>, enter a title and message, then <strong>"Send to All"</strong>. Every club member (except you) gets both an in-app notification and a push notification.</p>
            </Accordion>
          </SectionCard>

          {/* ── ADMIN SETTINGS ── */}
          <SectionCard id="admin-settings" title="Admin Settings" icon={Settings}>
            <P>The <strong>App Settings</strong> section on the Admin panel lets you toggle global field requirements. Changes apply immediately — no restart needed.</P>

            <Accordion title="What can I toggle?" defaultOpen>
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                {[
                  { name: 'Require Email', desc: 'Email mandatory for registration / player creation' },
                  { name: 'Require Phone', desc: 'Phone mandatory for registration / player creation' },
                  { name: 'Require Nickname', desc: 'Nickname mandatory for registration / player creation' },
                  { name: 'Unique Email', desc: 'Each email can only belong to one user' },
                  { name: 'Unique Phone', desc: 'Each phone can only belong to one user' },
                  { name: 'Unique Nickname', desc: 'Each nickname must be globally unique' },
                  { name: 'Require Skill Questionnaire', desc: 'New users must complete the 10-question skill quiz before using the app' },
                ].map(c => (
                  <div key={c.name} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                    <p className="text-sm font-semibold text-slate-800">{c.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{c.desc}</p>
                  </div>
                ))}
              </div>
            </Accordion>

            <Accordion title="Who can change these?">
              <p>Only users with the <strong>global admin role</strong> (not club admins). The toggles are stored in MongoDB, so they persist across restarts and apply to all clubs.</p>
            </Accordion>

            <Accordion title="What is the 'Wipe Database' option?">
              <p>A <strong>danger zone</strong> feature that permanently deletes ALL data (players, tournaments, games, ratings). You must type <code className="bg-red-50 text-red-700 px-1 rounded">WIPE EVERYTHING</code> to confirm. The admin account (admin / admin123) is re-seeded after wipe so you can still log in. Irreversible — intended only for complete resets.</p>
            </Accordion>
          </SectionCard>

          {/* ── FAQ ── */}
          <SectionCard id="faq" title="Frequently Asked Questions" icon={MessageCircle}>
            <Accordion title="What is the Skill Assessment questionnaire?" defaultOpen>
              <p>When you first sign up and log in, you'll be asked 10 questions about your badminton skills — things like your experience, shot consistency, court coverage, and competitive history. Based on your answers, the system calculates an initial rating (1200-1800). This rating is <strong>Provisional</strong> and will adjust quickly as you play real games.</p>
            </Accordion>
            <Accordion title="Can I be in multiple clubs?">
              <p>Yes! You can join as many clubs as you want. Your ratings are tracked separately per club (Club Rating), but your <strong>Lifetime Rating syncs automatically across all clubs</strong> — whenever you complete a rated game in any club, the Lifetime Rating update propagates to your profile in every other club you belong to. Fun tournament games don't count. Switch between clubs using the club selector at the top of the sidebar.</p>
            </Accordion>

            <Accordion title="Does playing in a Fun Tournament affect my rating?">
              <p>Not your Club or Lifetime Rating. Fun tournaments track <strong>tournament-internal standings</strong> so you can see how players rank within the event and games stay skill-balanced. But the results have <strong>zero impact</strong> on your Club Rating or Lifetime Rating — your permanent ratings only change in regular tournaments.</p>
            </Accordion>
            <Accordion title="What happens if a player doesn't show up?">
              <p>Admins can mark a player as <strong>absent</strong> from a specific game. The game can either be skipped or a <strong>forfeit penalty</strong> can be applied to the absent player's rating. Use this sparingly.</p>
            </Accordion>
            <Accordion title="Can I change my nickname later?">
              <p>Yes! Go to <strong>Settings</strong> in the sidebar and click the <strong>pencil icon</strong> next to your nickname. Enter a new one and click Save. The new nickname must be unique — no other player can have the same one.</p>
            </Accordion>
            <Accordion title="How do I reset my password?">
              <p>On the login page, click <strong>"Forgot password?"</strong>. Enter your registered email and you'll receive a reset link (if SMTP is configured). The link expires after 1 hour.</p>
            </Accordion>
            <Accordion title="Why don't I see the Players page?">
              <p>The Players page is only visible to <strong>Admins</strong> and <strong>Contributors</strong>. Regular players can see player information within tournaments and standings, but not the management page.</p>
            </Accordion>
            <Accordion title="What does 'On Bench' mean?">
              <p>When games are generated, some available players may not be assigned to a game (e.g., 10 players but only 2 courts = 8 players in games, 2 on bench). The "On Bench" section clearly shows who is waiting so the admin can plan the next round.</p>
            </Accordion>
            <Accordion title="Can kids play without email/phone?">
              <p>Yes! Use the <strong>Guest</strong> option when adding a player within a tournament. Guest players only need a name, nickname, username, and password — no email or phone required.</p>
            </Accordion>
            <Accordion title="How are doubles partners matched in 'Rotating' mode?">
              <p>The system uses a smart algorithm that considers: (1) Rating balance — tries to create evenly matched teams, (2) Pairing history — avoids repeating the same partnerships recently, (3) Court rotation — distributes across courts fairly.</p>
            </Accordion>
            <Accordion title="What does the 'Pending Invite' amber badge mean?">
              <p>When an admin invites a player via <strong>Find Players to Invite</strong>, an invite is stored in the database. The next time anyone opens that list, the player shows an amber <strong>"Pending Invite"</strong> badge instead of the green Invite button — so admins don't accidentally invite the same person twice. The badge clears when the player accepts or rejects.</p>
            </Accordion>
            <Accordion title="Why can some users only be added as Guests?">
              <p>Guests skip email/phone verification. They're ideal for kids or drop-in visitors who don't have an email address. Regular registered users (who signed up themselves) can be added directly — if you see a "Player not found" error, refresh the page — the system resolves existing accounts by both their user ID and their linked player record.</p>
            </Accordion>
            <Accordion title="Can I export data?">
              <p>Data export (standings, results to Excel/CSV) is planned for a future update. Stay tuned!</p>
            </Accordion>
            <Accordion title="What is the 'Wipe Database' option?">
              <p>Found in Admin settings, this is a <strong>danger zone</strong> feature that permanently deletes ALL data (players, tournaments, games, ratings). You must type "WIPE EVERYTHING" to confirm. This is irreversible and intended only for complete resets.</p>
            </Accordion>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
